import React, { useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material'
import { Formik, Form, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'

import { RootState, AppDispatch } from '../../../app/store'
import { updateUserProfile } from '../userSlice'
import { updateUserProfile as updateAuthProfile } from '../../auth/authSlice'

const validationSchema = Yup.object({
  first_name: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters'),
  last_name: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters'),
})

interface UpdateProfileFormProps {
  onSuccess?: () => void
}

interface FormValues {
  first_name: string
  last_name: string
}

const UpdateProfileForm: React.FC<UpdateProfileFormProps> = ({ onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useSelector((state: RootState) => state.auth)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  if (!user) {
    return null
  }

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    try {
      setIsLoading(true)
      setErrorMessage('')
      setSuccessMessage('')

      await dispatch(updateUserProfile(values)).unwrap()

      // Update auth state with new profile data
      dispatch(updateAuthProfile(values))

      setSuccessMessage('Profile updated successfully!')
      setTimeout(() => {
        setSuccessMessage('')
        onSuccess?.()
      }, 3000)
    } catch (error: any) {
      const message = error?.message || 'Failed to update profile. Please try again.'
      setErrorMessage(message)
    } finally {
      setIsLoading(false)
      setSubmitting(false)
    }
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Update Profile Information
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      <Formik
        initialValues={{
          first_name: user.first_name || '',
          last_name: user.last_name || '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, values, isValid, dirty, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={values.first_name}
                  onChange={(e) => setFieldValue('first_name', e.target.value)}
                  error={touched.first_name && Boolean(errors.first_name)}
                  helperText={touched.first_name && errors.first_name}
                  placeholder="Enter your first name"
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={values.last_name}
                  onChange={(e) => setFieldValue('last_name', e.target.value)}
                  error={touched.last_name && Boolean(errors.last_name)}
                  helperText={touched.last_name && errors.last_name}
                  placeholder="Enter your last name"
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={user.email}
                  disabled
                  helperText="Email cannot be changed"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoading || !isValid || !dirty}
                    sx={{ minWidth: 120 }}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Paper>
  )
}

export default UpdateProfileForm
